/**
 * Modelos para el módulo de configuración: empresa, roles, permisos y usuarios.
 * Restora360: roles admin, mesero, cocinero, cajero.
 */

export interface Usuario {
  id: string;
  nombre: string;
  email: string;
  rolId: string;
  activo: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Rol {
  id: string;
  nombre: string;
  descripcion: string;
  permisos: string[];
}

export interface PermisoDef {
  clave: string;
  nombre: string;
  modulo: string;
}

export interface ParametrosGenerales {
  nombreEmpresa: string;
  rut: string;
  notificacionesEmail: boolean;
  notificacionesPush: boolean;
  maxIntentosLogin: number;
  bloqueoMinutos: number;
}

export interface Personalizacion {
  tema: 'claro' | 'oscuro' | 'sistema';
  idioma: string;
  formatoFecha: string;
  zonaHoraria: string;
  filasPorPagina: number;
}

/** Catálogo de permisos del sistema Restora360, agrupados por módulo. Crear y editar son independientes. */
export const PERMISOS_DEF: PermisoDef[] = [
  { clave: 'dashboard_ver', nombre: 'Ver dashboard', modulo: 'Dashboard' },
  { clave: 'dashboard_ver_resumen', nombre: 'Ver resumen del día', modulo: 'Dashboard' },
  { clave: 'dashboard_ver_graficas', nombre: 'Ver gráficas', modulo: 'Dashboard' },
  { clave: 'dashboard_ver_ingresos', nombre: 'Ver ingresos y facturación en dashboard', modulo: 'Dashboard' },
  { clave: 'mesas_ver', nombre: 'Ver mesas y ocupación', modulo: 'Mesas' },
  { clave: 'mesas_crear', nombre: 'Crear mesas y zonas', modulo: 'Mesas' },
  { clave: 'mesas_editar', nombre: 'Editar/eliminar mesas y zonas', modulo: 'Mesas' },
  { clave: 'menu_ver', nombre: 'Ver menú', modulo: 'Menú' },
  { clave: 'menu_crear', nombre: 'Crear ítems en menú', modulo: 'Menú' },
  { clave: 'menu_editar', nombre: 'Editar ítems en menú', modulo: 'Menú' },
  { clave: 'pedidos_ver', nombre: 'Ver pedidos', modulo: 'Pedidos' },
  { clave: 'pedidos_crear', nombre: 'Crear pedidos', modulo: 'Pedidos' },
  { clave: 'pedidos_editar', nombre: 'Editar pedidos', modulo: 'Pedidos' },
  { clave: 'cocina_ver', nombre: 'Ver cocina (KDS)', modulo: 'Cocina' },
  { clave: 'cocina_editar', nombre: 'Gestionar preparación (marcar ítems listos)', modulo: 'Cocina' },
  { clave: 'facturacion_ver', nombre: 'Ver facturación', modulo: 'Facturación' },
  { clave: 'facturacion_crear', nombre: 'Generar facturas', modulo: 'Facturación' },
  { clave: 'facturacion_editar', nombre: 'Registrar pagos e imprimir facturas', modulo: 'Facturación' },
  { clave: 'reportes_ver', nombre: 'Ver reportes', modulo: 'Reportes' },
  { clave: 'insumos_ver', nombre: 'Ver insumos', modulo: 'Insumos' },
  { clave: 'configuracion_ver', nombre: 'Ver configuración', modulo: 'Configuración' },
  { clave: 'configuracion_usuarios_ver', nombre: 'Ver usuarios', modulo: 'Configuración' },
  { clave: 'configuracion_usuarios_crear', nombre: 'Crear usuarios', modulo: 'Configuración' },
  { clave: 'configuracion_usuarios_editar', nombre: 'Editar usuarios', modulo: 'Configuración' },
  { clave: 'configuracion_roles_ver', nombre: 'Ver roles y permisos', modulo: 'Configuración' },
  { clave: 'configuracion_roles_crear', nombre: 'Crear roles', modulo: 'Configuración' },
  { clave: 'configuracion_roles_editar', nombre: 'Editar roles y permisos', modulo: 'Configuración' },
  { clave: 'configuracion_parametros', nombre: 'Gestionar parámetros generales', modulo: 'Configuración' },
];

/** Roles iniciales con sus permisos (ids coinciden con AuthService: admin, mesero, cocinero, cajero). */
export const ROLES_INICIALES: Rol[] = [
  {
    id: 'admin',
    nombre: 'Administrador',
    descripcion: 'Acceso total al sistema',
    permisos: PERMISOS_DEF.map(p => p.clave),
  },
  {
    id: 'mesero',
    nombre: 'Mesero',
    descripcion: 'Mesas, menú y pedidos; sin crear/editar mesas ni configuración',
    permisos: [
      'dashboard_ver', 'dashboard_ver_resumen', 'dashboard_ver_graficas',
      'mesas_ver', 'menu_ver', 'pedidos_ver', 'pedidos_crear', 'pedidos_editar',
    ],
  },
  {
    id: 'cocinero',
    nombre: 'Cocinero',
    descripcion: 'Cocina y marcar ítems listos; pedidos en preparación',
    permisos: [
      'dashboard_ver', 'dashboard_ver_resumen', 'dashboard_ver_graficas',
      'pedidos_ver', 'cocina_ver', 'cocina_editar',
    ],
  },
  {
    id: 'cajero',
    nombre: 'Cajero',
    descripcion: 'Facturación, reportes e insumos; sin cocina ni edición de menú',
    permisos: [
      'dashboard_ver', 'dashboard_ver_resumen', 'dashboard_ver_graficas', 'dashboard_ver_ingresos',
      'mesas_ver', 'menu_ver', 'pedidos_ver',
      'facturacion_ver', 'facturacion_crear', 'facturacion_editar', 'reportes_ver', 'insumos_ver',
    ],
  },
];

export const IDIOMAS = [
  { value: 'es', label: 'Español' },
  { value: 'en', label: 'English' },
];

export const FORMATOS_FECHA = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/AAAA' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/AAAA' },
];

export const OPCIONES_TEMA = [
  { value: 'claro', label: 'Claro' },
  { value: 'oscuro', label: 'Oscuro' },
  { value: 'sistema', label: 'Según sistema' },
];
